import type { Theme } from '@mui/material';
import { alpha } from '@mui/material';

/**
 * A status colour and the tinted surface it sits on.
 *
 * Every colour in the panel comes from one of these or from `theme.appColors`
 * directly — no component states a colour of its own.
 */
export interface PlaybookTone {
  background: string;
  border: string;
  color: string;
}

export type PlaybookToneName =
  | 'failed'
  | 'muted'
  | 'neutral'
  | 'passed'
  | 'review'
  | 'reviewing'
  | 'running';

/**
 * Every status colour the panel uses, named by what the status means rather
 * than by the colour it happens to be. A keyed Record so a new tone is a
 * compile error at each visual decision instead of silently rendering as
 * something else.
 *
 * Picked by hand rather than derived from the palette roles, because a role is
 * chosen to be recognisable at full size and these have to survive as a tint
 * behind 12px text: `warning` is a light yellow that goes olive by the time it
 * is legible, and `success` is the brand teal, which reads as Aline rather
 * than as a pass. `review` is one notch stronger than the rest — it is the
 * call-to-action chip. Muted tones are for outcomes made irrelevant by a
 * resolution elsewhere on the rule, so they keep the quiet surface and only
 * drop the text.
 */
export const PLAYBOOK_TONES = {
  failed: { background: '#fef2f2', border: '#fecaca99', color: '#b91c1c' },
  muted: { background: '#f8fafc', border: '#e2e8f099', color: '#94a3b8' },
  neutral: { background: '#f8fafc', border: '#e2e8f099', color: '#64748b' },
  passed: { background: '#f0fdf4', border: '#bbf7d099', color: '#15803d' },
  review: { background: '#fef3c7', border: '#fcd34d99', color: '#b45309' },
  reviewing: { background: '#eef2ff', border: '#c7d2fe99', color: '#4338ca' },
  running: { background: '#eff6ff', border: '#bfdbfe99', color: '#1d4ed8' },
} as const satisfies Record<PlaybookToneName, PlaybookTone>;

/**
 * The border a card or an inset control draws when the hairline has to read as
 * an edge rather than as a divider — one step up from `theme.appColors.rule`,
 * which is what separates rows inside a card.
 */
export function getPlaybookStrongBorder(theme: Theme): string {
  return alpha(theme.appColors.iconsType, 0.25);
}
